import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./tooltip.css";

export default function Tooltip({ text }) {

   const box_ref = useRef( null );
   const tooltip_ref = useRef( null );
   const modal_ref = useRef( null );
   const [ show, setShow ] = useState( false );
   const [ showModal, setShowModal ] = useState( false );
   const [ isMobile, setIsMobile ] = useState( false );

   // Wykryj mobile
   useEffect(() => {
      const checkMobile = () => {
         setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
   }, []);

   // Przygotuj HTML - zamień \n na <br> jeśli tekst nie zawiera już tagów HTML
   const formattedHtml = useMemo(() => {
      if (!text) return null;
      
      // Zamień literalny string \n na <br>
      let html = text.replace(/\\n/g, '\n');
      
      // Jeśli tekst nie zawiera tagów HTML, zamień faktyczne \n na <br>
      // W przeciwnym razie zakładamy, że HTML już obsługuje formatowanie
      if (!/<[^>]+>/.test(html)) {
         html = html.replace(/\n/g, '<br>');
      }
      
      return html;
   }, [text]);

   useEffect(() => {
      if ( !box_ref.current ) return;
      if ( !tooltip_ref.current ) return;
      if (isMobile) return; // Na mobile nie pokazuj tooltipu
      
      const tooltip = tooltip_ref.current;
      
      if (!show) {
         // Ukryj tooltip gdy show jest false
         tooltip.style.display = "none";
         tooltip.style.visibility = "hidden";
         tooltip.style.opacity = "0";
         return;
      }

      const box_rect = box_ref.current.getBoundingClientRect();

      // Najpierw pokaż tooltip (ale poza ekranem), żeby móc zmierzyć jego rozmiar
      tooltip.style.display = "block";
      tooltip.style.visibility = "hidden";
      tooltip.style.opacity = "0";
      tooltip.style.top = "-9999px";
      tooltip.style.left = "-9999px";

      // Użyj requestAnimationFrame dla lepszej synchronizacji
      requestAnimationFrame(() => {
         setTimeout(() => {
            // Oblicz pozycję
            // getBoundingClientRect() zwraca pozycję względem viewportu
            // position: fixed też jest względem viewportu, więc nie trzeba dodawać scrollY/scrollX
            const tooltipHeight = tooltip.offsetHeight || 100;
            const tooltipWidth = tooltip.offsetWidth || 300;
            const spaceAbove = box_rect.top;
            const spaceBelow = window.innerHeight - box_rect.bottom;
            
            // Oblicz pozycję X - wyśrodkuj względem ikony
            const iconCenterX = box_rect.left + box_rect.width / 2;
            let left = iconCenterX - tooltipWidth / 2;
            
            // Upewnij się, że tooltip nie wychodzi poza lewą krawędź
            if (left < 10) {
               left = 10;
            }
            
            // Upewnij się, że tooltip nie wychodzi poza prawą krawędź
            if (left + tooltipWidth > window.innerWidth - 10) {
               left = window.innerWidth - tooltipWidth - 10;
            }
            
            // Oblicz pozycję Y - preferuj górę, jeśli jest miejsce
            let top;
            if (spaceAbove >= tooltipHeight + 10) {
               // Jest miejsce na górze - pokaż nad ikoną
               top = box_rect.top - tooltipHeight - 10;
            } else if (spaceBelow >= tooltipHeight + 10) {
               // Jest miejsce na dole - pokaż pod ikoną
               top = box_rect.bottom + 10;
            } else {
               // Nie ma miejsca ani na górze ani na dole - pokaż tam gdzie jest więcej miejsca
               if (spaceAbove > spaceBelow) {
                  top = 10; // Na górze viewportu
               } else {
                  top = window.innerHeight - tooltipHeight - 10; // Na dole viewportu
               }
            }
            
            // Upewnij się, że tooltip nie wychodzi poza viewport
            top = Math.max(10, Math.min(top, window.innerHeight - tooltipHeight - 10));

            tooltip.style.top = top + "px";
            tooltip.style.left = left + "px";
            tooltip.style.visibility = "visible";
            tooltip.style.opacity = "1";
         }, 50);
      });
   }, [ show, isMobile ]);

   useEffect(() => {
      if ( !box_ref.current ) return;

      if (isMobile) {
         // Na mobile użyj kliknięcia
         const onClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowModal(true);
         };

         box_ref.current.addEventListener("click", onClick);
         return () => box_ref.current?.removeEventListener("click", onClick);
      } else {
         // Na desktop użyj hover
         let timer;

         const onMouseLeave = () => {
            clearTimeout( timer );
            timer = setTimeout(() => setShow( false ), 300 )
         };

         const onMouseEnter = () => {
            clearTimeout( timer );
            timer = setTimeout(() => setShow( true ), 300 );
         }

         box_ref.current.addEventListener( "mouseleave", onMouseLeave );
         box_ref.current.addEventListener( "mouseenter", onMouseEnter );
         
         return () => {
            box_ref.current?.removeEventListener( "mouseleave", onMouseLeave );
            box_ref.current?.removeEventListener( "mouseenter", onMouseEnter );
         };
      }
   }, [isMobile]);

   // Zamknij modal po ESC
   useEffect(() => {
      if (!showModal) return;
      
      const onKeyDown = (e) => {
         if (e.key === 'Escape') {
            setShowModal(false);
         }
      };
      
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
   }, [showModal]);

   if (!text || (typeof text === 'string' && !text.replace(/<[^>]*>/g, '').trim())) return null;

   return (
      <>
         <span ref={ box_ref } className="tooltip-icon" style={{ cursor: isMobile ? 'pointer' : 'default' }}>
            <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
               <circle cx="15" cy="15" r="15" fill="#00258B"/>
               <path d="M14.9963 12.1C14.6003 12.1 14.2643 11.974 13.9883 11.722C13.7123 11.47 13.5743 11.152 13.5743 10.768C13.5743 10.372 13.7123 10.054 13.9883 9.814C14.2643 9.562 14.6003 9.436 14.9963 9.436C15.3923 9.436 15.7283 9.562 16.0043 9.814C16.2803 10.054 16.4183 10.372 16.4183 10.768C16.4183 11.152 16.2803 11.47 16.0043 11.722C15.7283 11.974 15.3923 12.1 14.9963 12.1ZM12.9263 22V21.658L13.1963 21.586C13.4363 21.514 13.5983 21.406 13.6823 21.262C13.7663 21.118 13.8143 20.926 13.8263 20.686V17.374C13.8263 17.002 13.8263 16.66 13.8263 16.348C13.8263 16.024 13.8203 15.694 13.8083 15.358C13.8083 15.118 13.7603 14.932 13.6643 14.8C13.5803 14.656 13.4183 14.56 13.1783 14.512L12.9263 14.458V14.134L16.0763 13.09L16.2923 13.288L16.2383 15.844V20.704C16.2383 20.944 16.2803 21.136 16.3643 21.28C16.4603 21.424 16.6223 21.532 16.8503 21.604L17.0483 21.658V22H12.9263Z" fill="white"/>
            </svg>
         </span>

         { !isMobile && createPortal(
            <div 
               ref={ tooltip_ref }
               className="zsk-tooltip" 
               dangerouslySetInnerHTML={{ __html: formattedHtml }}
            />
         , document.body )}

         { isMobile && showModal && createPortal(
            <>
               <div 
                  className="tooltip-modal-overlay"
                  onClick={() => setShowModal(false)}
               />
               <div 
                  ref={ modal_ref }
                  className="tooltip-modal"
               >
                  <div className="tooltip-modal-header">
                     <h3>Informacja</h3>
                     <button 
                        className="tooltip-modal-close"
                        onClick={() => setShowModal(false)}
                        aria-label="Zamknij"
                     >
                        <svg width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                           <path d="M2 17.5L18 1.5" stroke="#00258B" strokeWidth="3"/>
                           <path d="M2 1.5L18 17.5" stroke="#00258B" strokeWidth="3"/>
                        </svg>
                     </button>
                  </div>
                  <div 
                     className="tooltip-modal-content"
                     dangerouslySetInnerHTML={{ __html: formattedHtml }}
                  />
               </div>
            </>
         , document.body )}
      </>
   )
}